// Binary WebSocket frames: an 8-byte little-endian header, then the payload.
//
//   0  u8   kind    FrameKind
//   1  u8   codec   Codec
//   2  u16  stream  mic: voice session epoch | agent audio: utterance id | camera: 0 periodic, else request id
//   4  u32  seq     frame counter within the stream
//   8  ...  payload (PCM16 little-endian mono, or an encoded image)

export const FrameKind = {
  /** client -> server: microphone PCM16 at voice.ready.in.rate, ~20 ms per frame */
  MIC: 0x01,
  /** server -> client: agent PCM16 at 24 kHz, <= 100 ms per frame */
  AGENT_AUDIO: 0x02,
  /** client -> server: a webcam frame */
  CAMERA: 0x03,
  /** server -> client: mouth packets (reserved) */
  MOUTH: 0x04,
} as const;
export type FrameKind = (typeof FrameKind)[keyof typeof FrameKind];

export const Codec = {
  PCM16: 0x00,
  OPUS: 0x01,
  JPEG: 0x10,
  WEBP: 0x11,
  JSON: 0x20,
} as const;
export type Codec = (typeof Codec)[keyof typeof Codec];

export const HEADER_BYTES = 8;

/** Largest payloads the server accepts from a widget. */
export const MAX_FRAME_BYTES = {
  [FrameKind.MIC]: 16 * 1024,
  [FrameKind.CAMERA]: 512 * 1024,
} as Record<number, number>;

export interface Frame {
  kind: number;
  codec: number;
  stream: number;
  seq: number;
  payload: Uint8Array;
}

export function encodeFrame(
  kind: number,
  codec: number,
  stream: number,
  seq: number,
  payload: Uint8Array,
): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(HEADER_BYTES + payload.byteLength);
  const view = new DataView(out.buffer);
  view.setUint8(0, kind);
  view.setUint8(1, codec);
  view.setUint16(2, stream & 0xffff, true);
  view.setUint32(4, seq >>> 0, true);
  out.set(payload, HEADER_BYTES);
  return out;
}

/** The frame in `data`, or null if it's too short to be one. The payload is a view, not a copy. */
export function decodeFrame(data: ArrayBuffer | ArrayBufferView): Frame | null {
  const bytes =
    data instanceof ArrayBuffer ? new Uint8Array(data) : new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  if (bytes.byteLength < HEADER_BYTES) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  return {
    kind: view.getUint8(0),
    codec: view.getUint8(1),
    stream: view.getUint16(2, true),
    seq: view.getUint32(4, true),
    payload: bytes.subarray(HEADER_BYTES),
  };
}

/** Little-endian PCM16 bytes as samples (copies if the bytes aren't 2-byte aligned). */
export function bytesToInt16(bytes: Uint8Array): Int16Array {
  if (bytes.byteOffset % 2 === 0) return new Int16Array(bytes.buffer, bytes.byteOffset, bytes.byteLength >> 1);
  return new Int16Array(bytes.slice().buffer, 0, bytes.byteLength >> 1);
}

export function int16ToBytes(samples: Int16Array): Uint8Array {
  return new Uint8Array(samples.buffer, samples.byteOffset, samples.byteLength);
}

/** Milliseconds of audio in `bytes` of PCM16 mono at `rate`. */
export const pcmMs = (bytes: number, rate: number) => (bytes / 2 / rate) * 1000;
